/**
 * User panel: collapsible card top-right with display name (editable via wallet sig),
 * reward counters (tokens + leaderboard points), and claim button (USDC payout).
 */

import { getWalletSessionToken } from './wallet-auth.js';
import { requestShare, type ArenaResultClientPayload } from '../share/share-client.js';
import { track } from '../analytics.js';

const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

interface PlayerStats {
  walletAddress: string;
  displayName: string | null;
  tokens: number;
  leaderboardPoints: number;
  /** PR-3: claimable rewards from finished arenas, distinct from
   *  the lifetime `leaderboardPoints` cumulative ranking number. */
  pendingLbTokens: number;
  /** Last arena's payout summary; toast renders this when unread. */
  lastArenaResult: ArenaResult | null;
  totalExtractions: number;
  totalMatches: number;
}

interface ArenaResult {
  arenaId: string;
  lbPoints: number;
  lbTokens: number;
  position: number;
  totalPlayers: number;
  /** Legacy USDC pool snapshot — populated by pre-migration arenas
   *  (arena_distribute RPC). Stays for back-compat. */
  poolUsdcRaw?: string;
  /** Token-denominated pool — populated by post-migration arenas
   *  (arena_distribute_token). Same units as `lbTokens`. */
  totalPoolToken?: string;
  totalGameFeeToken?: string;
  endedAt: string;
  unread: boolean;
  /** Stake multiplier this player paid for the arena (1/2/5/10).
   *  Populated by migration 018+. Absent on older arenas → treat as 1. */
  stakeMultiplier?: number;
  /** Top standings snapshot (post-migration 016). Top-8 by lb_points
   *  DESC; absent on pre-016 arenas — share modal hides the standings
   *  thumbnail in that case. */
  topStandings?: Array<{
    wallet: string;
    displayName: string | null;
    position: number;
    lbPoints: number;
    kills: number;
    /** Per-row stake multiplier — populated by migration 018+. */
    stakeMultiplier?: number;
  }>;
}

interface ClaimConfig {
  /** 'native' on mainnet (1:1 with players.tokens raw), 'usdc' on devnet
   *  (legacy rate conversion). */
  mode: 'native' | 'usdc';
  mint: string;
  /** Decimals of the payout asset (6 for USDC, varies for native). */
  decimals: number;
  /** Display ticker — what the UI labels the payout as. */
  symbol: string;
  cluster: string;
  /** Only meaningful when mode='usdc'. Ignored on native (rate is 1:1). */
  rateUsdcRawPerToken: string;
  minTokens: string;
}

/** Convert a raw `tokens` balance (as stored in players.tokens) to the
 *  human-readable payout amount, using the active claim mode.
 *  - native: tokens IS raw of the native mint. Divide by 10^decimals.
 *  - usdc:   multiply tokens by rate, then divide by 10^decimals (USDC). */
function formatPayout(tokens: number, cfg: ClaimConfig): string {
  if (cfg.mode === 'native') {
    const human = tokens / 10 ** cfg.decimals;
    if (human === 0) return '0';
    // Tiered precision so big balances read like money and tiny ones
    // don't round to 0. Thousands separator on the >= 1 branch.
    if (human >= 1) {
      return human.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (human >= 0.01) {
      return human.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    }
    return human.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  }
  const rate = BigInt(cfg.rateUsdcRawPerToken || '5000');
  const usdcRaw = BigInt(tokens) * rate;
  const human = Number(usdcRaw) / 10 ** cfg.decimals;
  return human.toFixed(3);
}

let currentWallet: string | null = null;
let currentStats: PlayerStats | null = null;
let claimConfig: ClaimConfig | null = null;

/** Wire the panel — call after auth resolves. */
export function initPlayerPanel(): void {
  const profile = document.getElementById('user-profile');
  const pill = document.getElementById('user-pill');
  if (!profile || !pill) return;

  // Expose the refresh hook on window so the inline live-arena poll in
  // index.html can trigger it when it detects an arenaId rotation
  // (= the previous arena ended). Without this, the post-match toast
  // only appears after a manual page reload.
  (window as any).__refreshPlayerPanel = (wallet: string | null) => refreshPlayerPanel(wallet);

  pill.addEventListener('click', (e) => {
    e.stopPropagation();
    profile.classList.toggle('expanded');
  });

  // Phaser's input system listens for `pointerdown` (and touch equivalents)
  // on the canvas/document level — it fires BEFORE `click`. If the user
  // taps inside the expanded panel, Phaser can see the same event and
  // trigger a scene button behind it (e.g. the "Agents" portal). Swallow
  // pointer events originating inside the panel at the earliest stage so
  // Phaser never sees them.
  const swallow = (e: Event) => e.stopPropagation();
  ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach((evt) => {
    profile.addEventListener(evt, swallow);
  });

  document.addEventListener('click', (e) => {
    if (!profile.contains(e.target as Node)) {
      profile.classList.remove('expanded');
    }
  });

  document.getElementById('panel-name-edit-btn')?.addEventListener('click', startNameEdit);
  document.getElementById('claim-btn')?.addEventListener('click', startClaim);
  document.getElementById('lb-claim-btn')?.addEventListener('click', startLbClaim);
  document.getElementById('arena-toast-close')?.addEventListener('click', dismissArenaToast);
  document.getElementById('arena-toast-share')?.addEventListener('click', (e) => { void openShareForLastArena(e); });

  // Pre-load claim config (cheap, public)
  fetch(`${SERVER_HTTP_URL}/api/claims/config`)
    .then((r) => r.json())
    .then((cfg: ClaimConfig) => {
      claimConfig = cfg;
      // Stats may have landed before config — re-render readouts now
      // that we know mode/decimals/symbol, so the raw native units get
      // formatted instead of shown as huge integers.
      if (currentStats) setCounters(currentStats.tokens, currentStats.pendingLbTokens);
      renderClaimRow();
      renderLbClaimRow();
    })
    .catch(() => { /* offline ok */ });
}

/** Refresh stats from server. Call after login or after a state-changing action. */
export async function refreshPlayerPanel(walletAddress: string | null): Promise<void> {
  currentWallet = walletAddress;

  if (!walletAddress) {
    // X-login (no wallet) — hide claim & edit since they're wallet-bound
    hideElement('panel-name-edit-btn');
    hideElement('claim-btn');
    setNameDisplay((window as any).__currentUser?.name || 'Player');
    setCounters(0, 0);
    setWalletShort('');
    return;
  }

  showElement('panel-name-edit-btn');
  showElement('claim-btn');
  setWalletShort(walletAddress);

  try {
    const sessionToken = getWalletSessionToken(walletAddress);
    if (!sessionToken) return;
    const resp = await fetch(`${SERVER_HTTP_URL}/api/players/me?wallet=${encodeURIComponent(walletAddress)}`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
    if (!resp.ok) return;
    const stats: PlayerStats = await resp.json();
    currentStats = stats;

    const name = stats.displayName || shortenAddress(walletAddress);
    setNameDisplay(name);

    // Sync to global user object (used at room join, leaderboard, etc.)
    if ((window as any).__currentUser) {
      (window as any).__currentUser.name = name;
      const pillName = document.getElementById('user-name');
      if (pillName) pillName.textContent = name;
    }

    // Pill + panel show *claimable* LB rewards (pendingLbTokens),
    // not the lifetime cumulative leaderboard_points (which still
    // backs the all-time leaderboard view).
    setCounters(stats.tokens, stats.pendingLbTokens);
    renderClaimRow();
    renderLbClaimRow();
    syncUnreadIndicators(stats.lastArenaResult);
    maybeShowArenaToast(stats.lastArenaResult);
  } catch (err) {
    console.warn('Failed to refresh player stats:', err);
  }
}

// ============ Renderers ============

function setNameDisplay(name: string): void {
  const el = document.getElementById('panel-name-display');
  if (el) el.textContent = name;
}

function setCounters(tokens: number, pendingLbTokens: number): void {
  const setText = (id: string, v: string) => {
    const el = document.getElementById(id);
    if (el) el.textContent = v;
  };
  // On native (post token-economy migration), tokens / pendingLbTokens
  // are raw native-token units. Format via formatPayout so 6-decimal
  // SNK doesn't render as 3,396,226,416. Devnet (mode='usdc') keeps
  // the legacy small-integer display.
  const native = claimConfig?.mode === 'native';
  const symbol = claimConfig?.symbol ?? '';
  const fmt = (v: number) => (claimConfig && native) ? formatPayout(v, claimConfig) : v.toLocaleString();
  const fmtWithSymbol = (v: number) => native ? `${fmt(v)} ${symbol}` : fmt(v);
  // Pill chips stay compact (icon implies units); panel cards show symbol.
  setText('pill-tokens-value', fmt(tokens));
  setText('pill-points-value', fmt(pendingLbTokens));
  setText('panel-tokens', fmtWithSymbol(tokens));
  setText('panel-points', fmtWithSymbol(pendingLbTokens));
}

function renderLbClaimRow(): void {
  const section = document.getElementById('lb-claim-section');
  const amount  = document.getElementById('lb-claim-amount');
  const btn     = document.getElementById('lb-claim-btn') as HTMLButtonElement | null;
  if (!section || !amount || !btn) return;

  const pending = currentStats?.pendingLbTokens ?? 0;
  const native = claimConfig?.mode === 'native';
  const symbol = claimConfig?.symbol ?? '';
  const display = (claimConfig && native) ? formatPayout(pending, claimConfig) : pending.toLocaleString();
  amount.textContent = native ? `${display} ${symbol}` : display;
  if (pending > 0) {
    section.classList.add('visible');
    btn.disabled = false;
    // Native: LB rewards and tokens share denomination — drop "→ TOKENS".
    btn.textContent = native ? `CLAIM ${display} ${symbol}` : `CLAIM ${display} LB → TOKENS`;
  } else {
    section.classList.remove('visible');
  }
}

function syncUnreadIndicators(result: ArenaResult | null): void {
  const pillPoints = document.getElementById('pill-points');
  if (!pillPoints) return;
  if (result?.unread) pillPoints.classList.add('unread');
  else                pillPoints.classList.remove('unread');
}

function setWalletShort(addr: string): void {
  const el = document.getElementById('panel-wallet');
  if (el) el.textContent = addr ? shortenAddress(addr) : '';
}

function renderClaimRow(): void {
  const tokenAmt = document.getElementById('claim-tokens-amount');
  const payoutAmt = document.getElementById('claim-payout-amount');
  const btn = document.getElementById('claim-btn') as HTMLButtonElement | null;
  if (!btn) return;

  const tokens = currentStats?.tokens ?? 0;
  const minTokens = claimConfig ? Number(claimConfig.minTokens) : 100;
  const symbol = claimConfig?.symbol ?? 'USDC';

  // Round down to whole min units (we claim everything they have, not partial)
  const claimable = tokens >= minTokens ? tokens : 0;
  const payoutDisplay = claimConfig ? formatPayout(claimable, claimConfig) : '0';

  if (tokenAmt) tokenAmt.textContent = claimable.toLocaleString();
  if (payoutAmt) payoutAmt.textContent = `${payoutDisplay} ${symbol}`;

  if (claimable >= minTokens) {
    btn.disabled = false;
    btn.textContent = `CLAIM ${payoutDisplay} ${symbol}`;
  } else if (tokens > 0) {
    btn.disabled = true;
    btn.textContent = `MIN ${minTokens} TOKENS TO CLAIM`;
  } else {
    btn.disabled = true;
    btn.textContent = 'NO TOKENS YET';
  }
}

// ============ Helpers ============

function setStatus(elementId: string, msg: string, kind: 'info' | 'error' | 'success' = 'info'): void {
  const el = document.getElementById(elementId);
  if (!el) return;
  el.textContent = msg;
  el.classList.remove('error', 'success');
  if (kind === 'error') el.classList.add('error');
  if (kind === 'success') el.classList.add('success');
}

function setStatusLink(
  elementId: string,
  msg: string,
  href: string,
  label: string,
  kind: 'info' | 'error' | 'success' = 'info'
): void {
  const el = document.getElementById(elementId);
  if (!el) return;
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = label;
  el.replaceChildren(document.createTextNode(msg), link);
  el.classList.remove('error', 'success');
  if (kind === 'error') el.classList.add('error');
  if (kind === 'success') el.classList.add('success');
}

function shortenAddress(addr: string): string {
  return addr.length > 10 ? addr.slice(0, 4) + '...' + addr.slice(-4) : addr;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function hideElement(id: string): void {
  const el = document.getElementById(id);
  if (el) (el as HTMLElement).style.display = 'none';
}
function showElement(id: string): void {
  const el = document.getElementById(id);
  if (el) (el as HTMLElement).style.display = '';
}

interface SolanaProvider {
  signMessage(msg: Uint8Array): Promise<{ signature: Uint8Array }>;
  publicKey: { toString(): string } | null;
  isConnected: boolean;
  connect(): Promise<{ publicKey: { toString(): string } }>;
}
function getProvider(): SolanaProvider | null {
  const win = window as any;
  if (win.phantom?.solana?.isPhantom) return win.phantom.solana;
  if (win.solflare?.isSolflare) return win.solflare;
  if (win.solana) return win.solana;
  return null;
}

async function signCanonicalMessage(walletAddress: string, message: string): Promise<{ signature: string; timestamp: number }> {
  const provider = getProvider();
  if (!provider) throw new Error('No wallet detected');
  if (!provider.publicKey) await provider.connect();

  const messageBytes = new TextEncoder().encode(message);
  const { signature } = await provider.signMessage(messageBytes);
  return {
    signature: btoa(String.fromCharCode(...signature)),
    timestamp: Date.now(),
  };
}

// ============ Edit name flow ============

function startNameEdit(e: Event): void {
  e.stopPropagation();
  if (!currentWallet) {
    setStatus('panel-name-status', 'Connect a wallet to change your name', 'error');
    return;
  }

  const row = document.getElementById('panel-name-row');
  if (!row) return;

  const currentName = currentStats?.displayName || shortenAddress(currentWallet);
  const originalHTML = row.innerHTML;

  row.innerHTML = `
    <input id="panel-name-input" type="text" maxlength="24" value="${escapeHtml(currentName)}" />
    <button id="panel-name-save" style="background:#00ccff;color:#001a22;border:none;font-family:inherit;font-size:11px;font-weight:700;padding:6px 12px;border-radius:6px;cursor:pointer;letter-spacing:0.4px;">SAVE</button>
    <button id="panel-name-cancel" style="background:none;border:none;color:rgba(255,255,255,0.4);font-family:inherit;font-size:18px;cursor:pointer;line-height:1;padding:0 4px;">×</button>
  `;

  const input = document.getElementById('panel-name-input') as HTMLInputElement;
  const saveBtn = document.getElementById('panel-name-save');
  const cancelBtn = document.getElementById('panel-name-cancel');

  input?.focus();
  input?.select();

  const restore = () => {
    row.innerHTML = originalHTML;
    document.getElementById('panel-name-edit-btn')?.addEventListener('click', startNameEdit);
  };
  const cancel = () => { restore(); setStatus('panel-name-status', ''); };

  cancelBtn?.addEventListener('click', cancel);

  saveBtn?.addEventListener('click', async () => {
    const newName = input.value.trim();
    if (newName.length < 1 || newName.length > 24) {
      setStatus('panel-name-status', 'Name must be 1-24 chars', 'error');
      return;
    }
    if (!/^[a-zA-Z0-9 _\-.]+$/.test(newName)) {
      setStatus('panel-name-status', 'Letters, digits, space, _, -, . only', 'error');
      return;
    }

    setStatus('panel-name-status', 'Sign in your wallet…');
    saveBtn.setAttribute('disabled', 'true');

    try {
      const message = `Pump My Snake — change display name to "${newName}" at ${Date.now()}`;
      const ts = Date.now();
      const canonicalMessage = `Pump My Snake — change display name to "${newName}" at ${ts}`;
      const provider = getProvider();
      if (!provider) throw new Error('No wallet');
      if (!provider.publicKey) await provider.connect();
      const messageBytes = new TextEncoder().encode(canonicalMessage);
      const { signature } = await provider.signMessage(messageBytes);
      const sigB64 = btoa(String.fromCharCode(...signature));

      const resp = await fetch(`${SERVER_HTTP_URL}/api/players/me/name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: currentWallet, newName, signature: sigB64, timestamp: ts }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }

      setStatus('panel-name-status', 'Name updated', 'success');
      await refreshPlayerPanel(currentWallet);
      restoreFromStats();
    } catch (err: any) {
      setStatus('panel-name-status', err?.message || 'Failed', 'error');
      saveBtn.removeAttribute('disabled');
    }
  });

  input?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') saveBtn?.dispatchEvent(new Event('click'));
    if (ev.key === 'Escape') cancel();
  });
}

function restoreFromStats(): void {
  const row = document.getElementById('panel-name-row');
  if (!row) return;
  row.innerHTML = `
    <span id="panel-name-display">${escapeHtml(currentStats?.displayName || '')}</span>
    <button id="panel-name-edit-btn">EDIT</button>
  `;
  document.getElementById('panel-name-edit-btn')?.addEventListener('click', startNameEdit);
}

// ============ LB rewards claim flow ============

async function startLbClaim(e: Event): Promise<void> {
  e.stopPropagation();
  if (!currentWallet || !currentStats) return;
  if ((currentStats.pendingLbTokens ?? 0) <= 0) return;

  const btn = document.getElementById('lb-claim-btn') as HTMLButtonElement;
  btn.disabled = true;
  setStatus('lb-claim-status', 'Sign in your wallet to convert…');

  try {
    const ts = Date.now();
    const canonicalMessage = `Pump My Snake — claim leaderboard rewards at ${ts}`;
    const provider = getProvider();
    if (!provider) throw new Error('No wallet');
    if (!provider.publicKey) await provider.connect();
    const { signature } = await provider.signMessage(new TextEncoder().encode(canonicalMessage));
    const sigB64 = btoa(String.fromCharCode(...signature));

    const resp = await fetch(`${SERVER_HTTP_URL}/api/players/me/lb-claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress: currentWallet, signature: sigB64, timestamp: ts }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    setStatus('lb-claim-status', `+${data.tokensAdded} tokens`, 'success');
    await refreshPlayerPanel(currentWallet);
  } catch (err: any) {
    setStatus('lb-claim-status', err?.message || 'Claim failed', 'error');
    btn.disabled = false;
  }
}

// ============ Match-end toast ============

function maybeShowArenaToast(result: ArenaResult | null): void {
  const toast = document.getElementById('arena-toast');
  if (!toast || !result || !result.unread) return;

  const headline = document.getElementById('arena-toast-headline');
  const points   = document.getElementById('arena-toast-points');
  const tokens   = document.getElementById('arena-toast-tokens');
  const pool     = document.getElementById('arena-toast-pool');
  const stake    = document.getElementById('arena-toast-stake');

  // Stake badge — only show for high-stakes plays. x1 is the default
  // and would just be noise next to the kicker.
  if (stake) {
    const m = result.stakeMultiplier ?? 1;
    if (m > 1) {
      stake.textContent = `x${m} STAKE`;
      stake.classList.add('visible');
    } else {
      stake.classList.remove('visible');
    }
  }

  if (headline) {
    const rank = document.createElement('strong');
    rank.textContent = `#${result.position}`;
    headline.replaceChildren(
      document.createTextNode('You placed '),
      rank,
      document.createTextNode(` of ${result.totalPlayers}`),
    );
  }
  if (points)   points.textContent = result.lbPoints.toLocaleString();
  if (tokens) {
    // result.lbTokens is raw native units post-migration (mainnet) /
    // legacy small-int (devnet). Mirror the panel formatter.
    const native = claimConfig?.mode === 'native';
    const symbol = claimConfig?.symbol ?? '';
    const display = (claimConfig && native) ? formatPayout(result.lbTokens, claimConfig) : result.lbTokens.toLocaleString();
    tokens.textContent = native ? `+${display} ${symbol}` : `+${display}`;
  }
  if (pool) {
    // Prefer the post-migration token-denominated pool. Fall back to the
    // legacy USDC raw for arenas that distributed via the old RPC.
    const symbol = claimConfig?.symbol ?? 'USDC';
    const decimals = claimConfig?.decimals ?? 6;
    try {
      const rawStr = result.totalPoolToken ?? result.poolUsdcRaw ?? '0';
      const raw = BigInt(rawStr);
      const divisor = 10n ** BigInt(decimals);
      const whole = raw / divisor;
      const frac  = (raw % divisor).toString().padStart(decimals, '0').slice(0, 3);
      pool.textContent = `${whole.toLocaleString()}.${frac} ${symbol}`;
    } catch { pool.textContent = `0.000 ${symbol}`; }
  }
  toast.classList.add('visible');
}

/** One-click share from the arena toast: build the personal-card
 *  payload (with USD value if we can fetch the live rate), POST to
 *  /api/share/generate, open the Twitter intent. No modal step. */
async function openShareForLastArena(e: Event): Promise<void> {
  e.stopPropagation();
  const result = currentStats?.lastArenaResult;
  if (!result) return;
  track('share_clicked', { type: 'arena_result', position: result.position, total: result.totalPlayers });
  const decimals = claimConfig?.decimals ?? 6;
  const symbol = claimConfig?.symbol ?? 'TOKEN';

  const btn = document.getElementById('arena-toast-share') as HTMLButtonElement | null;
  const originalText = btn?.textContent ?? 'SHARE ON X';
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'GENERATING…';
  }

  // Best-effort USD calc — if the quote endpoint is reachable and
  // returns the femto-USDC rate, include the USD-micro equivalent so
  // the card shows "≈ $X.XX" alongside the SNK amount. Missing rate
  // just hides the line; no hard dependency.
  let usdMicroValue: string | undefined;
  try {
    const quoteResp = await fetch(`${SERVER_HTTP_URL}/api/payments/quote`);
    if (quoteResp.ok) {
      const data = await quoteResp.json();
      const femtoPerRaw = data?.native?.femtoUsdcPerRaw;
      if (typeof femtoPerRaw === 'string' && femtoPerRaw !== '0') {
        // tokens (raw) × femtoUSDC/raw = femto-USDC total. Divide by
        // 1e9 to get micro-USDC (1 USD = 1e6 micro = 1e15 femto).
        const tokensRaw = BigInt(result.lbTokens ?? 0);
        const femtoTotal = tokensRaw * BigInt(femtoPerRaw);
        usdMicroValue = (femtoTotal / 1_000_000_000n).toString();
      }
    }
  } catch (err) {
    console.warn('[share] USD rate fetch failed, omitting USD line:', err);
  }

  const arenaResult: ArenaResultClientPayload = {
    type: 'arena-result',
    position: result.position,
    totalPlayers: result.totalPlayers,
    tokensRaw: String(result.lbTokens ?? '0'),
    poolRaw: String(result.totalPoolToken ?? result.poolUsdcRaw ?? '0'),
    lbPoints: result.lbPoints,
    symbol,
    decimals,
    usdMicroValue,
  };

  try {
    await requestShare(arenaResult);
  } catch (err: any) {
    console.error('[share] generate failed:', err);
    if (btn) { btn.disabled = false; btn.textContent = originalText; }
    return;
  }

  if (btn) {
    btn.disabled = false;
    btn.textContent = '✓ SHARED';
    setTimeout(() => { if (btn) btn.textContent = originalText; }, 3000);
  }
}

async function dismissArenaToast(e: Event): Promise<void> {
  e.stopPropagation();
  const toast = document.getElementById('arena-toast');
  toast?.classList.remove('visible');
  if (!currentWallet) return;
  try {
    const ts = Date.now();
    const canonicalMessage = `Pump My Snake — ack arena result at ${ts}`;
    const provider = getProvider();
    if (!provider) throw new Error('No wallet');
    if (!provider.publicKey) await provider.connect();
    const { signature } = await provider.signMessage(new TextEncoder().encode(canonicalMessage));
    const sigB64 = btoa(String.fromCharCode(...signature));

    // Fire-and-forget — server marks last_arena_result.unread=false.
    // Next refresh won't trigger the toast or the trophy pulse again.
    fetch(`${SERVER_HTTP_URL}/api/players/me/ack-arena-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress: currentWallet, signature: sigB64, timestamp: ts }),
    }).catch(() => {});
  } catch {
    // If the wallet prompt is cancelled, the local UI can still hide it.
    // The server will keep the result unread and may show it again later.
  }
  // Optimistically clear the local pulse + flag so a second refresh
  // doesn't re-show the toast before the server write lands.
  if (currentStats?.lastArenaResult) currentStats.lastArenaResult.unread = false;
  syncUnreadIndicators(currentStats?.lastArenaResult ?? null);
}

// ============ Claim flow ============

async function startClaim(e: Event): Promise<void> {
  e.stopPropagation();
  if (!currentWallet || !currentStats || !claimConfig) return;

  const tokens = currentStats.tokens;
  const minTokens = Number(claimConfig.minTokens);
  if (tokens < minTokens) return;

  track('claim_started', { tokens });

  const btn = document.getElementById('claim-btn') as HTMLButtonElement;
  btn.disabled = true;
  setStatus('claim-status', 'Sign in your wallet to authorize…');

  try {
    const ts = Date.now();
    const canonicalMessage = `Pump My Snake — claim ${tokens} tokens at ${ts}`;
    const provider = getProvider();
    if (!provider) throw new Error('No wallet');
    if (!provider.publicKey) await provider.connect();
    const { signature } = await provider.signMessage(new TextEncoder().encode(canonicalMessage));
    const sigB64 = btoa(String.fromCharCode(...signature));

    const symbol = claimConfig?.symbol ?? 'USDC';
    setStatus('claim-status', `Sending ${symbol}… (~13s for finalization)`);

    const resp = await fetch(`${SERVER_HTTP_URL}/api/players/me/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress: currentWallet,
        tokensToClaim: tokens,
        signature: sigB64,
        timestamp: ts,
      }),
    });

    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    const txSignature = String(data.txSignature || '');
    if (!/^[1-9A-HJ-NP-Za-km-z]{32,128}$/.test(txSignature)) {
      throw new Error('Malformed tx signature');
    }
    const cluster = claimConfig.cluster === 'mainnet-beta' ? '' : `?cluster=${encodeURIComponent(claimConfig.cluster)}`;
    const link = `https://explorer.solana.com/tx/${txSignature}${cluster}`;
    setStatusLink('claim-status', 'Sent! ', link, 'View tx', 'success');
    track('claim_completed', { tokens, tx: txSignature });

    // Refresh balance
    await refreshPlayerPanel(currentWallet);
  } catch (err: any) {
    track('claim_failed', { tokens, reason: err?.message || 'unknown' });
    setStatus('claim-status', err?.message || 'Claim failed', 'error');
    btn.disabled = false;
  }
}
